import moment from 'moment-timezone'
import { isString, endsWith } from 'lodash-es'
import { zeroPad } from '.'

/** *
 * Formats number of seconds into HH:mm:ss with seconds being optional
 * @param {string|number} totalSeconds Number of seconds
 * @param {bool} displaySeconds
 * @returns {string}
 */
export const humanizeSeconds = (totalSeconds, displaySeconds = true) => {
  const seconds = isString(totalSeconds)
    ? parseFloat(totalSeconds)
    : totalSeconds
  const duration = moment.duration(seconds, 'seconds')
  const result = []

  if (duration.days()) {
    result.push(`${duration.days()}`)
  }

  if (duration.hours()) {
    result.push(`${duration.hours()}h`)
  }

  if (duration.minutes()) {
    result.push(`${duration.minutes()}m`)
  }

  if (displaySeconds) {
    result.push(`${duration.seconds()}s`)
  }

  return result.join(' ')
}

/**
 * Formats number of seconds into hh:mm:ss
 * @param {number} sec
 * @returns {string}
 */
export const formatDuration = (sec) => {
  // TODO (noahlaux): We would like to transition to Number.isNaN, but we have too much view logic
  // depending on how the window.isNaN implementation works
  // eslint-disable-next-line no-restricted-properties
  if (window.isNaN(sec) || sec < 0) {
    return ''
  }

  const time = Math.round(sec)

  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = time % 60

  let timeString = ''
  if (hours) {
    timeString = `${hours < 10 ? '0' : ''}${hours}:`
  }
  timeString = `${timeString}${minutes < 10 ? '0' : ''}${minutes}:`
  timeString = `${timeString}${seconds < 10 ? '0' : ''}${seconds}`

  return timeString
}

export const formatDurationWithMilliseconds = (sec) => {
  // TODO (noahlaux): We would like to transition to Number.isNaN, but we have too much view logic
  // depending on how the window.isNaN implementation works
  // eslint-disable-next-line no-restricted-properties
  if (window.isNaN(sec) || sec < 0) {
    return ''
  }

  // kill milliseconds so they don't affect the output from formatDuration
  /* eslint-disable-next-line no-bitwise */
  const minSec = formatDuration(sec | 0)
  const milliseconds = sec.toString().split('.')[1]
  return `${minSec}.${milliseconds || 0}`
}

/* copied from formatting.js, needs a test */
export const formatDate = (rawMs) => {
  const ms = parseInt(rawMs, 10)
  if (Number.isNaN(ms) || ms < 0) {
    return 'Invalid Date'
  }

  const d = new Date(ms)

  const v = [
    d.getFullYear(),
    zeroPad(d.getMonth() + 1, 2),
    zeroPad(d.getDate(), 2),
  ]

  return v.join('-')
}

export const humanizeTime = (timeStr) => {
  let timeString = timeStr

  if (!endsWith(timeString, 'Z')) {
    timeString += 'Z'
  }

  return moment(timeString).format('HH:mm:ss')
}

/**
 * Formats date as relative if it is not longer than a day ago,
 * otherwise absolute. Does not include time.
 * @param dateString
 * @returns {string}
 */
export const humanizeRelativeDate = (dateString) => {
  const dt = moment(dateString)

  if (moment().diff(dt, 'days') > 1) {
    return dt.format('ddd MMM D YYYY')
  }

  return dt.fromNow()
}

/**
 * Converts unix seconds timestamp like 1491927622 into
 * an ISO timestamp like 2017-04-11T16:20:22
 * @param timestamp
 */
export const unixTimestampToISO = (timestamp) => {
  return moment.unix(timestamp).utc().format('YYYY-MM-DDTHH:mm:ss')
}

export const parseUTCUnixSecondsTime = (timeString) => {
  const utcDateSeconds = new Date(new Date().toUTCString()) / 1000
  return utcDateSeconds - timeString
}

export const getLocalTime = (timeInTimezone) => {
  if (!moment.isMoment(timeInTimezone)) {
    return ''
  }

  const cloneTimeInTimezone = timeInTimezone.clone()
  const localTimezone = moment.tz.guess()
  return moment.tz(cloneTimeInTimezone, localTimezone).format('HH:mm')
}
